import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ordersAPI } from '../services/api';
import { useAlert } from '../context/AlertContext';
import { FiPlus, FiEdit, FiTrash2, FiShoppingBag, FiFilter } from 'react-icons/fi';
import { format } from 'date-fns';

const Orders = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get('status') || '';
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const alert = useAlert();

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const response = await ordersAPI.getAll(statusFilter);
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      alert.push({ type: 'error', title: 'Load failed', message: 'Could not load orders.' });
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleDelete = async (id, garmentType) => {
    if (window.confirm(`Are you sure you want to delete this ${garmentType} order?`)) {
      try {
        await ordersAPI.delete(id);
        fetchOrders();
        alert.push({ type: 'success', title: 'Order deleted' });
      } catch (error) {
        console.error('Error deleting order:', error);
        alert.push({ type: 'error', title: 'Delete failed', message: 'Please try again.' });
      }
    }
  };

  const handleStatusFilter = (status) => {
    if (status === '') {
      setSearchParams({});
    } else {
      setSearchParams({ status });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Orders</h1>
          <p className="text-gray-600 mt-2">Manage customer orders</p>
        </div>
        <Link
          to="/orders/new"
          className="mt-4 sm:mt-0 bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2"
        >
          <FiPlus size={20} />
          <span>New Order</span>
        </Link>
      </div>

      {/* Status Filter */}
      <div className="mb-6 flex flex-wrap gap-2">
        <button
          onClick={() => handleStatusFilter('')}
          className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 ${
            statusFilter === '' ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
          }`}
        >
          <FiFilter size={18} />
          <span>All</span>
        </button>
        <button
          onClick={() => handleStatusFilter('pending')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            statusFilter === 'pending' ? 'bg-yellow-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
          }`}
        >
          Pending
        </button>
        <button
          onClick={() => handleStatusFilter('in_progress')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            statusFilter === 'in_progress' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
          }`}
        >
          In Progress
        </button>
        <button
          onClick={() => handleStatusFilter('completed')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            statusFilter === 'completed' ? 'bg-green-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
          }`}
        >
          Completed
        </button>
        <button
          onClick={() => handleStatusFilter('cancelled')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            statusFilter === 'cancelled' ? 'bg-red-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
          }`}
        >
          Cancelled
        </button>
      </div>

      {orders.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <FiShoppingBag className="mx-auto text-gray-400" size={48} />
          <p className="text-gray-600 mt-4">No orders found</p>
          <Link
            to="/orders/new"
            className="inline-block mt-4 text-primary-600 hover:text-primary-700 font-medium"
          >
            Create your first order
          </Link>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Garment
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Order Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Delivery Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Price
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {orders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link
                        to={`/customers/${order.customer_id}`}
                        className="text-primary-600 hover:text-primary-700 font-medium"
                      >
                        {order.customer_name || `Customer #${order.customer_id}`}
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-900">
                      {order.garment_type} {order.quantity > 1 && `(x${order.quantity})`}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                      {format(new Date(order.order_date), 'MMM dd, yyyy')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                      {order.delivery_date ? format(new Date(order.delivery_date), 'MMM dd, yyyy') : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                        order.status === 'completed' ? 'bg-green-100 text-green-800' :
                        order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        order.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-gray-100 font-medium">
                      {order.price ? `PKR ${order.price.toFixed(0)}` : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <Link
                          to={`/orders/${order.id}/edit`}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          <FiEdit size={18} />
                        </Link>
                        <button
                          onClick={() => handleDelete(order.id, order.garment_type)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <FiTrash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;

